const chapter = {
  id: 'periodic-classification',
  slug: 'periodic-classification',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Classification of Elements & Periodicity',
        hindi: 'तत्वों का वर्गीकरण और आवर्तता',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'history',
      title: {
        english: 'Development of the Periodic Table',
        hindi: 'आवर्त सारणी का विकास',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Döbereiner\'s triads** (1817): groups of 3 elements with similar properties; middle element\'s atomic mass ≈ mean of other two (Li, Na, K).',
        hindi: '**डोबेराइनर के त्रिक** (1817): समान गुणों वाले 3 तत्वों के समूह; मध्य तत्व का परमाणु द्रव्यमान ≈ अन्य दो (Li, Na, K) का माध्य।',
            },
            {
              english: '**Newlands\' law of octaves** (1865): every 8th element shows similar properties — fails after Ca.',
        hindi: '**न्यूलैंड्स का अष्टक नियम** (1865): प्रत्येक 8वाँ तत्व समान गुण दिखाता है - Ca के बाद विफल हो जाता है।',
            },
            {
              english: '**Mendeleev\'s table** (1869): based on atomic mass; left gaps for undiscovered elements (eka-Si = Ge, eka-Al = Ga). Defects: position of H, isotopes, anomalous pairs (Ar–K, Co–Ni, Te–I).',
        hindi: '**मेंडेलीव की तालिका** (1869): परमाणु द्रव्यमान पर आधारित; अनदेखे तत्वों के लिए छोड़े गए अंतराल (eka-Si = Ge, eka-Al = Ga)। दोष: H की स्थिति, आइसोटोप, विषम जोड़े (Ar-K, Co-Ni, Te-I)।',
            },
            {
              english: '**Modern periodic law** (Moseley, 1913): properties of elements are periodic functions of their **atomic number** $Z$ (not mass). Resolved Mendeleev\'s anomalies.',
        hindi: '**आधुनिक आवर्त नियम** (मोसले, 1913): तत्वों के गुण उनके **परमाणु क्रमांक** $Z$ (द्रव्यमान नहीं) के आवर्त फलन हैं। मेंडेलीव की विसंगतियों का समाधान किया।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Long-form periodic table: 18 groups, 7 periods.',
        hindi: 'दीर्घ-रूप आवर्त सारणी: 18 समूह, 7 आवर्त।',
            },
            {
              english: 'Block classification by last subshell filled: **s-block** (Gp 1, 2 + He), **p-block** (Gp 13–18), **d-block** (Gp 3–12), **f-block** (Lanthanides + Actinides).',
        hindi: 'अंतिम भरे गए उपकोश द्वारा ब्लॉक वर्गीकरण: **एस-ब्लॉक** (जीपी 1, 2 + हे), **पी-ब्लॉक** (जीपी 13-18), **डी-ब्लॉक** (जीपी 3-12), **एफ-ब्लॉक** (लैंथेनाइड्स + एक्टिनाइड्स)।',
            },
            {
              english: 'Period number = principal QN $n$ of valence shell. Number of elements per period: 2, 8, 8, 18, 18, 32, 32.',
        hindi: 'आवर्त संख्या = संयोजकता कोश का मूल QN $n$। प्रति आवर्त में तत्वों की संख्या: 2, 8, 8, 18, 18, 32, 32.',
            },
          ],
        },
      ],
    },
    {
      id: 'iupac-naming',
      title: {
        english: 'IUPAC Names of Super-heavy Elements',
        hindi: 'अति भारी तत्वों के IUPAC नाम',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'For elements beyond $Z = 100$, IUPAC suggested temporary names from atomic number digits using Latin/Greek roots; final names assigned after confirmation.',
        hindi: '$Z = 100$ से आगे के तत्वों के लिए, IUPAC ने Latin/Greek जड़ों का उपयोग करके परमाणु संख्या अंकों से अस्थायी नाम सुझाए; पुष्टि के बाद अंतिम नाम सौंपे गए।',
          },
        },
        {
          type: 'table',
          headers: ['Digit', 'Root', 'Symbol'],
          rows: [
            ['0', 'nil', 'n'],
            ['1', 'un', 'u'],
            ['2', 'bi', 'b'],
            ['3', 'tri', 't'],
            ['4', 'quad', 'q'],
            ['5', 'pent', 'p'],
            ['6', 'hex', 'h'],
            ['7', 'sept', 's'],
            ['8', 'oct', 'o'],
            ['9', 'enn', 'e'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'End the name with "-ium". Example: $Z = 119$ ⇒ Un-un-enn-ium (Uue). Confirmed names include nihonium (113), moscovium (115), tennessine (117), oganesson (118).',
        hindi: 'नाम को "-ium" से समाप्त करें। उदाहरण: $Z = 119$ ⇒ Un-un-enn-ium (Uue)। पुष्टि किए गए नामों में निहोनियम (113), मोस्कोवियम (115), टेनेसीन (117), ओगेनेसन (118) शामिल हैं।',
          },
        },
      ],
    },
    {
      id: 'atomic-radius',
      title: {
        english: 'Atomic & Ionic Radii',
        hindi: 'परमाणु और आयनिक त्रिज्याएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Types: **covalent radius** (half the distance between two like atoms in a single covalent bond), **van der Waals radius** (closest approach without bonding — for noble gases), **metallic radius** (half internuclear distance in metallic crystal). Order: vdW > metallic > covalent for same element.',
        hindi: 'प्रकार: **सहसंयोजक त्रिज्या** (एकल सहसंयोजक बंधन में दो समान परमाणुओं के बीच की आधी दूरी), **वैन डेर वाल्स त्रिज्या** (बिना बंधन के निकटतम दृष्टिकोण - महान गैसों के लिए), **धात्विक त्रिज्या** (धातु क्रिस्टल में आधी आंतरिक परमाणु दूरी)। क्रम: vdW > धात्विक > समान तत्व के लिए सहसंयोजक।',
            },
            {
              english: '**Across a period** (L → R): atomic radius **decreases** (effective nuclear charge $Z_{\\text{eff}}$ rises while same shell).',
        hindi: '**एक अवधि में** (एल → आर): परमाणु त्रिज्या **घटती है** (प्रभावी परमाणु चार्ज $Z_{\\text{eff}}$ समान शेल के दौरान बढ़ता है)।',
            },
            {
              english: '**Down a group**: atomic radius **increases** (new shell added; outweighs $Z_{\\text{eff}}$ rise).',
        hindi: '**एक समूह में नीचे**: परमाणु त्रिज्या **बढ़ती है** (नया शेल जोड़ा गया; $Z_{\\text{eff}}$ वृद्धि से अधिक)।',
            },
            {
              english: 'Lanthanide contraction: gradual decrease in size of 4f elements ⇒ 4d and 5d transition elements have nearly the same size (e.g. Zr ≈ Hf).',
        hindi: 'लैंथेनाइड संकुचन: 4f तत्वों के आकार में क्रमिक कमी ⇒ 4d और 5d संक्रमण तत्वों का आकार लगभग समान होता है (जैसे Zr ≈ Hf)।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Cation** is smaller than parent atom (loss of valence shell, increased $Z_{\\text{eff}}$).',
        hindi: '**धनायन** मूल परमाणु से छोटा है (संयोजकता कोश की हानि, बढ़ी हुई $Z_{\\text{eff}}$)।',
            },
            {
              english: '**Anion** is larger than parent atom (added e⁻, more inter-electronic repulsion).',
        hindi: '**आयन** मूल परमाणु से बड़ा है (e⁻ जोड़ा गया, अधिक अंतर-इलेक्ट्रॉनिक प्रतिकर्षण)।',
            },
            {
              english: '**Iso-electronic species** (same electrons, different $Z$): radius **decreases** as $Z$ increases. e.g. N³⁻ > O²⁻ > F⁻ > Na⁺ > Mg²⁺ > Al³⁺.',
        hindi: '**आइसो-इलेक्ट्रॉनिक प्रजातियां** (समान इलेक्ट्रॉन, भिन्न $Z$): $Z$ बढ़ने पर त्रिज्या **घटती** है। जैसे N³⁻ > O²⁻ > F⁻ > Na⁺ > Mg²⁺ > Al³⁺.',
            },
          ],
        },
      ],
    },
    {
      id: 'ie-ea',
      title: {
        english: 'Ionisation Enthalpy & Electron-Gain Enthalpy',
        hindi: 'आयनन एन्थैल्पी और इलेक्ट्रॉन-ग्रहण एन्थैल्पी',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Ionisation enthalpy (IE)',
        hindi: 'आयनीकरण एन्थैल्पी (आईई)',
          },
          body: {
            english: 'Energy required to remove the most loosely bound electron from one mole of isolated gaseous atoms. Always positive.',
        hindi: 'पृथक गैसीय परमाणुओं के एक मोल से सबसे शिथिल रूप से बंधे इलेक्ट्रॉन को निकालने के लिए आवश्यक ऊर्जा। हमेशा ही सकारात्मक।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Across a period**: IE **increases** (smaller size, higher $Z_{\\text{eff}}$).',
        hindi: '**एक अवधि के दौरान**: IE **बढ़ता है** (छोटा आकार, अधिक $Z_{\\text{eff}}$)।',
            },
            {
              english: '**Down a group**: IE **decreases** (larger size, more screening).',
        hindi: '**एक समूह में नीचे**: IE **घटता है** (बड़ा आकार, अधिक स्क्रीनिंग)।',
            },
            {
              english: 'Anomalies (period 2): B < Be (loss of $2p^1$ vs stable $2s^2$); O < N (paired $2p^4$ less stable than half-filled $2p^3$). Same pattern repeats: Al < Mg, S < P.',
        hindi: 'विसंगतियाँ (अवधि 2): बी <बीई ($2p^1$ बनाम स्थिर $2s^2$ का नुकसान); O < N (युग्मित $2p^4$ आधे भरे $2p^3$ से कम स्थिर)। समान पैटर्न दोहराता है: अल <एमजी, एस <पी।',
            },
            {
              english: 'Successive IE always increase: IE₁ < IE₂ < IE₃ … Big jump when a noble-gas core is reached.',
        hindi: 'क्रमिक IE हमेशा बढ़ता है: IE₁ < IE₂ < IE₃ ... एक महान-गैस कोर तक पहुंचने पर बड़ी छलांग।',
            },
            {
              english: 'Used to deduce group: e.g. Mg has very large jump after IE₂ (group 2).',
        hindi: 'समूह निकालने के लिए प्रयुक्त: उदा. IE₂ (समूह 2) के बाद Mg में बहुत बड़ी छलांग है।',
            },
          ],
        },
        {
          type: 'definition',
          term: {
            english: 'Electron-gain enthalpy ($\\Delta_{eg} H$)',
        hindi: 'इलेक्ट्रॉन-लाभ एन्थैल्पी ($\\Delta_{eg} H$)',
          },
          body: {
            english: 'Enthalpy change when an electron is added to a neutral isolated gaseous atom. Usually negative (energy released).',
        hindi: 'जब एक तटस्थ पृथक गैसीय परमाणु में एक इलेक्ट्रॉन जोड़ा जाता है तो एन्थैल्पी बदल जाती है। आमतौर पर नकारात्मक (ऊर्जा जारी)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Across a period**: $|\\Delta_{eg} H|$ generally increases (more negative).',
        hindi: '**एक अवधि के दौरान**: $|\\Delta_{eg} H|$ आम तौर पर बढ़ता है (अधिक नकारात्मक)।',
            },
            {
              english: '**Down a group**: $|\\Delta_{eg} H|$ decreases.',
        hindi: '**एक समूह में नीचे**: $|\\Delta_{eg} H|$ घटता है।',
            },
            {
              english: 'Halogens have the most negative $\\Delta_{eg} H$ (1 e⁻ short of noble-gas configuration). Within halogens: F has less negative $\\Delta_{eg} H$ than Cl due to small size and inter-electronic repulsion. Order: Cl > F > Br > I.',
        hindi: 'हैलोजन में सबसे नकारात्मक $\\Delta_{eg} H$ (नोबल-गैस कॉन्फ़िगरेशन से 1 e⁻ कम) होता है। हैलोजन के भीतर: छोटे आकार और अंतर-इलेक्ट्रॉनिक प्रतिकर्षण के कारण F में Cl की तुलना में कम नकारात्मक $\\Delta_{eg} H$ है। आदेश: सीएल > एफ > बीआर > आई।',
            },
            {
              english: 'Noble gases: $\\Delta_{eg} H > 0$ (added electron must enter next shell).',
        hindi: 'उत्कृष्ट गैसें: $\\Delta_{eg} H > 0$ (अतिरिक्त इलेक्ट्रॉन को अगले कोश में प्रवेश करना होगा)।',
            },
          ],
        },
      ],
    },
    {
      id: 'electronegativity',
      title: {
        english: 'Electronegativity & Metallic / Non-metallic Character',
        hindi: 'इलेक्ट्रोनगेटिविटी और धात्विक / गैर-धातु गुण',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Electronegativity (EN)',
        hindi: 'इलेक्ट्रोनगेटिविटी (EN)',
          },
          body: {
            english: 'Tendency of an atom in a molecule to attract bonded electrons towards itself. Unlike IE/EA it has no precise unit; popular scale: **Pauling** (F = 4.0).',
        hindi: 'किसी अणु में परमाणु की बंधित इलेक्ट्रॉनों को अपनी ओर आकर्षित करने की प्रवृत्ति। IE/EA के विपरीत इसकी कोई सटीक इकाई नहीं है; लोकप्रिय पैमाना: **पॉलिंग** (F = 4.0)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Across period EN **increases**, down group EN **decreases**.',
        hindi: 'संपूर्ण अवधि में EN **बढ़ता है**, नीचे समूह EN **घटता है**।',
            },
            {
              english: 'Order of common atoms: F > O > N ≈ Cl > Br > C ≈ S ≈ I > H > metals.',
        hindi: 'सामान्य परमाणुओं का क्रम: F > O > N ≈ Cl > Br > C ≈ S ≈ I > H > धातुएँ।',
            },
            {
              english: '**Metallic character**: ability to lose electrons. Decreases across period, increases down group.',
        hindi: '**धात्विक गुण**: इलेक्ट्रॉन खोने की क्षमता। अवधि के दौरान घटती है, समूह की ओर बढ़ती है। **गैर-धात्विक चरित्र**: विपरीत प्रवृत्ति।',
            },
            {
              english: '**Non-metallic character**: opposite trend.',
        hindi: '**गैर-धात्विक चरित्र**: विपरीत प्रवृत्ति।',
            },
            {
              english: 'Most metallic element: Cs (or Fr). Most non-metallic / most electronegative: F.',
        hindi: 'अधिकांश धात्विक तत्व: Cs (या Fr)। सर्वाधिक अधात्विक / सर्वाधिक विद्युत ऋणात्मक: एफ.',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Diagonal relationship (period-2 vs period-3 below diagonally): Li ↔ Mg, Be ↔ Al, B ↔ Si — owing to similar size/charge ratio.',
        hindi: 'विकर्ण संबंध (विकर्ण से नीचे अवधि-2 बनाम अवधि-3): ली ↔ एमजी, बीई ↔ अल, बी ↔ सी - समान size/charge अनुपात के कारण।',
          },
        },
      ],
    },
    {
      id: 'periodicity-properties',
      title: {
        english: 'Periodicity in Other Properties',
        hindi: 'अन्य गुणधर्मों में आवर्तता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Valency** (with respect to H or O): goes 1, 2, 3, 4, 3, 2, 1 across a period — controlled by valence electrons.',
        hindi: '**संयोजकता** (एच या ओ के संबंध में): एक अवधि में 1, 2, 3, 4, 3, 2, 1 जाती है - वैलेंस इलेक्ट्रॉनों द्वारा नियंत्रित।',
            },
            {
              english: 'Nature of oxides: across a period basic → amphoteric → acidic. e.g. Na₂O (basic), Al₂O₃ (amphoteric), SO₃ / Cl₂O₇ (acidic).',
        hindi: 'ऑक्साइड की प्रकृति: एक अवधि में क्षारीय → उभयधर्मी → अम्लीय। जैसे Na₂O (बेसिक), Al₂O₃ (एम्फोटेरिक), SO₃/Cl₂O₇ (अम्लीय)।',
            },
            {
              english: '**Reactivity**: most reactive metals on lower-left, most reactive non-metals on upper-right (excluding noble gases).',
        hindi: '**प्रतिक्रियाशीलता**: निचले-बाएँ पर सबसे अधिक प्रतिक्रियाशील धातुएँ, ऊपरी-दाएँ पर सबसे अधिक प्रतिक्रियाशील गैर-धातुएँ (उत्कृष्ट गैसों को छोड़कर)।',
            },
            {
              english: 'Anomalous behaviour of 2nd-period elements (Li, Be, B, C, N, O, F): small size, high charge density, no $d$-orbitals, restrict covalency to 4.',
        hindi: 'दूसरी अवधि के तत्वों (Li, Be, B, C, N, O, F) का असामान्य व्यवहार: छोटा आकार, उच्च चार्ज घनत्व, कोई $d$-ऑर्बिटल्स नहीं, सहसंयोजकता को 4 तक सीमित करें।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'Z_{\\text{eff}} = Z - \\sigma\\ \\text{(Slater)}',
      desc: {
        english: 'Effective nuclear charge',
        hindi: 'प्रभावी परमाणु चार्ज',
      },
    },
    {
      latex: 'r_{\\text{ion}}\\ \\downarrow\\ \\text{as}\\ Z\\uparrow\\ \\text{(iso-electronic)}',
      desc: {
        english: 'Iso-electronic radius rule',
        hindi: 'आइसो-इलेक्ट्रॉनिक त्रिज्या नियम',
      },
    },
    {
      latex: '\\text{IE}\\ \\uparrow\\ \\text{across period},\\ \\downarrow\\ \\text{down group}',
      desc: {
        english: 'IE trend',
        hindi: 'IE रुझान',
      },
    },
    {
      latex: '|\\Delta_{eg} H|:\\ \\text{Cl} > \\text{F} > \\text{Br} > \\text{I}',
      desc: {
        english: 'Halogen EA order',
        hindi: 'हलोजन ईए ऑर्डर',
      },
    },
    {
      latex: '\\text{EN}\\ \\uparrow\\ \\text{across period}',
      desc: {
        english: 'Electronegativity trend',
        hindi: 'इलेक्ट्रोनगेटिविटी प्रवृत्ति',
      },
    },
    {
      latex: '\\text{Metallic}\\ \\downarrow\\ \\text{across period}',
      desc: {
        english: 'Metallic character',
        hindi: 'धात्विक लक्षण',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Iso-electronic',
      problem: {
        english: 'Correct order of ionic radii: O²⁻, F⁻, Na⁺, Mg²⁺?',
        hindi: 'आयनिक त्रिज्या का सही क्रम: O²⁻, F⁻, Na⁺, Mg²⁺?',
      },
      answer: {
        english: 'O²⁻ > F⁻ > Na⁺ > Mg²⁺',
        hindi: 'O²⁻ > F⁻ > Na⁺ > Mg²⁺',
      },
      solution: {
        english: 'All have 10 e⁻; size $\\downarrow$ as nuclear charge $\\uparrow$.',
        hindi: 'सभी के पास 10 e⁻ हैं; आकार $\\downarrow$ परमाणु चार्ज के रूप में $\\uparrow$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'IE',
      problem: {
        english: 'Element with the highest IE₁ among Li, Be, B, C, N?',
        hindi: 'ली, बीई, बी, सी, एन के बीच उच्चतम IE₁ वाला तत्व?',
      },
      answer: {
        english: 'N',
        hindi: 'एन',
      },
      solution: {
        english: 'Across period IE rises; N has half-filled $2p^3$ (extra stable).',
        hindi: 'अवधि के दौरान IE बढ़ता है; N में आधा भरा हुआ $2p^3$ (अतिरिक्त स्थिर) है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'EA',
      problem: {
        english: 'Electron-gain enthalpy is most negative for:',
        hindi: 'इलेक्ट्रॉन-लाभ एन्थैल्पी इसके लिए सबसे नकारात्मक है:',
      },
      answer: {
        english: 'Cl',
        hindi: 'सीएल',
      },
      solution: {
        english: 'Cl > F > Br > I (small F suffers e⁻e⁻ repulsion).',
        hindi: 'Cl > F > Br > I (छोटा F e⁻e⁻ प्रतिकर्षण झेलता है)।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Periodic',
      problem: {
        english: 'Which oxide is amphoteric: Na₂O, MgO, Al₂O₃, SO₃?',
        hindi: 'कौन सा ऑक्साइड उभयधर्मी है: Na₂O, MgO, Al₂O₃, SO₃?',
      },
      answer: {
        english: 'Al₂O₃',
        hindi: 'Al₂O₃',
      },
      solution: {
        english: 'Reacts with both acids and bases.',
        hindi: 'अम्ल और क्षार दोनों के साथ प्रतिक्रिया करता है।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'EN',
      problem: {
        english: 'Most electronegative element?',
        hindi: 'सर्वाधिक विद्युत ऋणात्मक तत्व?',
      },
      answer: {
        english: 'F',
        hindi: 'एफ',
      },
      solution: {
        english: 'Pauling 4.0.',
        hindi: 'पॉलिंग 4.0.',
      },
    },
  ],
  next: {
    slug: 'redox-reactions',
    title: 'Redox Reactions',
  },
};

export default chapter;
